import clsx from 'clsx'
import { cloneElement, type HTMLAttributes } from 'react'
import { TOGGLE_COLORS } from './constants'
import type { ToggleColor, ToggleOption } from './types'

const containerColorStyles: Record<ToggleColor, string> = {
  brand: 'border-brand-200',
  neutral: 'border-border-strong',
}

const selectedItemStyles: Record<ToggleColor, string> = {
  brand: 'bg-surface-brand-selected text-brand-800',
  neutral: 'bg-surface-selected-strong text-text-primary',
}

const unselectedItemHoverStyles: Record<ToggleColor, string> = {
  brand: 'hover:bg-surface-brand-hover-strong',
  neutral: 'hover:bg-surface-hover',
}

export type ToggleProps<Value extends string = string> = {
  /** Accessible name for the toggle group. */
  'aria-label': string
  /** The two options to switch between. */
  options: [ToggleOption<Value>, ToggleOption<Value>]
  /** The currently selected option's value. */
  value: Value
  /** Called with an option's value when it's clicked. */
  onChange: (value: Value) => void
  /**
   * Color treatment.
   * @default 'brand'
   */
  color?: ToggleColor
  /**
   * Show icon + text labels, or icons only (compact/mobile treatment).
   * @default true
   */
  showLabels?: boolean
}

/**
 * Toggle Component
 *
 * A two-option segmented control (tablist with two tabs) following the Paxton design system.
 *
 * @example
 * <Toggle
 *   aria-label="File source"
 *   value={viewMode}
 *   onChange={setViewMode}
 *   options={[
 *     { value: 'external', label: 'Box', icon: <BoxLogo /> },
 *     { value: 'matter', label: 'Paxton' },
 *   ]}
 * />
 *
 * @example
 * // Icon-only (compact/mobile) treatment
 * <Toggle aria-label="View mode" showLabels={false} value={view} onChange={setView} options={viewOptions} />
 */
export function Toggle<Value extends string = string>({
  'aria-label': ariaLabel,
  options,
  value,
  onChange,
  color = TOGGLE_COLORS.BRAND,
  showLabels = true,
}: ToggleProps<Value>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-ds-component="Toggle"
      data-ds-color={color}
      data-ds-variant={showLabels ? 'labeled' : 'icon-only'}
      className={clsx(
        'inline-flex shrink-0 items-center gap-0.5 border bg-surface-field',
        containerColorStyles[color],
        showLabels ? 'rounded-full p-[3px]' : 'min-h-[28px] rounded-md px-[3px] py-0.5'
      )}
    >
      {options.map((option) => {
        const isSelected = option.value === value
        const iconSource = showLabels ? option.icon : option.compactIcon ?? option.icon
        const icon = iconSource
          ? cloneElement(iconSource, {
              className: clsx(showLabels ? 'h-4 w-4' : 'h-[18px] w-[18px]', 'shrink-0', iconSource.props.className),
              'aria-hidden': true,
            } as Pick<HTMLAttributes<HTMLElement>, 'className' | 'aria-hidden'>)
          : null

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-label={showLabels ? undefined : option.label}
            onClick={() => onChange(option.value)}
            className={clsx(
              'flex shrink-0 items-center font-semibold',
              'focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
              isSelected ? selectedItemStyles[color] : clsx('text-true-gray-600', unselectedItemHoverStyles[color]),
              showLabels ? 'gap-1 whitespace-nowrap rounded-full px-2 py-px text-sm leading-5 tracking-[-0.15px]' : 'justify-center rounded p-0.5'
            )}
          >
            {icon}
            {showLabels && option.label}
          </button>
        )
      })}
    </div>
  )
}
